import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    Typography,
    CircularProgress,
    Box,
    Divider,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useMediaQuery,
    useTheme
} from '@mui/material';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/apiConfig';

const T = { primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd', tertiary: '#635983', tertiaryContainer: '#dbcdff', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb', text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5', emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2', amber: '#f59e0b', amberBg: '#fef3c7', font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif', shadow: '0 1px 3px rgba(45,51,53,0.04)' };
const Icon = ({ name, size = 24, sx = {} }) => (<span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>);

// Standaard categorie volgorde
const CATEGORIES = [
    'Cameras', 'Drones', 'Lens', 'Monitors', 'Statief', 'Geluid', 'Grip', 'Licht',
    'Camera Accessoires', 'Special effects', 'Studio equipment', 'Vervoer en karren'
];

const CopyMaterialsDialog = ({
    open,
    onClose,
    currentDraaidag,
    onCopy,
    reservations = []
}) => {
    const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
    const [selectedDraaidag, setSelectedDraaidag] = useState(null);
    const [selectedMaterials, setSelectedMaterials] = useState([]);
    const [availabilityMap, setAvailabilityMap] = useState({});
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [draaidagMaterials, setDraaidagMaterials] = useState({});
    const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    // Filter draaidagen die materialen hebben
    const draaidagenWithMaterials = useMemo(() => {
        // Groepeer reservations per draaidag
        const draaidagMap = {};

        reservations.forEach(reservation => {
            if (reservation.fields?.IDdraaidagOutlook?.[0] && reservation.fields?.Materiaal && Array.isArray(reservation.fields.Materiaal) && reservation.fields.Materiaal.length > 0) {
                const draaidagId = reservation.fields.IDdraaidagOutlook[0];

                if (!draaidagMap[draaidagId]) {
                    draaidagMap[draaidagId] = {
                        id: draaidagId,
                        naam: reservation.fields.DraaidagNaamOutlook?.[0] || reservation.fields.DraaidagNaam?.[0] || 'Onbekende draaidag',
                        datum: reservation.fields.Start,
                        materiaalIds: new Set()
                    };
                }

                // Voeg materiaal IDs toe
                reservation.fields.Materiaal.forEach(matId => {
                    draaidagMap[draaidagId].materiaalIds.add(matId);
                });
            }
        });

        // Converteer naar array en filter huidige draaidag eruit
        return Object.values(draaidagMap)
            .filter(d => d.id !== currentDraaidag?.id)
            .sort((a, b) => new Date(b.datum) - new Date(a.datum)); // Sorteer op datum (nieuwste eerst)
    }, [reservations, currentDraaidag]);

    // Filter draaidagen op zoekterm
    const filteredDraaidagen = useMemo(() => {
        if (!searchTerm) return draaidagenWithMaterials;

        const lowerSearch = searchTerm.toLowerCase();
        return draaidagenWithMaterials.filter(d =>
            d.naam.toLowerCase().includes(lowerSearch) ||
            d.datum.includes(searchTerm)
        );
    }, [draaidagenWithMaterials, searchTerm]);

    // Groepeer materialen per categorie
    const materialsByCategory = useMemo(() => {
        const grouped = {};

        // Initialiseer alle categorieën
        CATEGORIES.forEach(cat => {
            grouped[cat] = [];
        });

        // Groepeer materialen
        if (selectedDraaidag) {
            Array.from(selectedDraaidag.materiaalIds).forEach(materialId => {
                const material = draaidagMaterials[materialId];
                if (material && material.fields.Categorie) {
                    const category = material.fields.Categorie;
                    if (!grouped[category]) {
                        grouped[category] = [];
                    }
                    grouped[category].push({ id: materialId, material });
                }
            });
        }

        // Filter lege categorieën
        return Object.entries(grouped)
            .filter(([_, materials]) => materials.length > 0)
            .reduce((acc, [cat, materials]) => {
                acc[cat] = materials;
                return acc;
            }, {});
    }, [selectedDraaidag, draaidagMaterials]);

    // Toggle categorie accordion
    const handleToggleCategory = (category) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    // Check beschikbaarheid van materialen (gebruik dezelfde logica als SimpleMaterialWizard)
    function checkAvailability(materialIds, materials = null) {
        if (!currentDraaidag || materialIds.length === 0) return;
        setIsCheckingAvailability(true);
        try {
            const availability = {};
            const draaidagStart = new Date(currentDraaidag.fields.StartDatum || currentDraaidag.fields.Start);
            const draaidagEnd = new Date(currentDraaidag.fields.EindDatum || currentDraaidag.fields.Eind);

            // Gebruik dezelfde logica als SimpleMaterialWizard
            materialIds.forEach(materialId => {
                const materialsToCheck = materials || draaidagMaterials;
                const material = materialsToCheck[materialId];
                const materialReservations = (processReservationsData && processReservationsData[materialId]) || [];
                const hasConflict = materialReservations.some(reservation => {
                    if (reservation.reservationId === currentDraaidag.currentReservationId) return false;
                    const resStart = new Date(reservation.startDate);
                    const resEnd = new Date(reservation.endDate);
                    return (resStart <= draaidagEnd && resEnd >= draaidagStart);
                });
                availability[materialId] = !hasConflict;
            });
            setAvailabilityMap(availability);
            const availableMaterials = materialIds.filter(id => availability[id]);
            setSelectedMaterials(availableMaterials);
        } finally {
            setIsCheckingAvailability(false);
        }
    }

    // Laad materialen voor geselecteerde draaidag
    useEffect(() => {
        if (!selectedDraaidag || !open) return;

        const loadMaterials = async () => {
            setIsLoadingMaterials(true);
            try {
                // Haal materiaal details op voor de geselecteerde draaidag via backend
                const materialIds = Array.from(selectedDraaidag.materiaalIds);
                if (materialIds.length === 0) {
                    setDraaidagMaterials({});
                    return;
                }

                // Haal materialen op via backend (security: API key niet in frontend)
                const response = await axios.get(
                    `${BACKEND_URL}/Api/materialen/batch`,
                    { params: { ids: materialIds.join(',') } }
                );

                // Maak een map van materiaal ID naar materiaal object
                const materials = {};
                response.data.forEach(record => {
                    materials[record.id] = record;
                });

                setDraaidagMaterials(materials);

                // Check beschikbaarheid direct na laden, geef materials mee
                await checkAvailability(materialIds, materials);

                // Open alle categorieën standaard
                setExpandedCategories(new Set(CATEGORIES));

            } catch (error) {
                console.error('Error loading materials:', error);
            } finally {
                setIsLoadingMaterials(false);
            }
        };

        loadMaterials();
    }, [selectedDraaidag, open, currentDraaidag]);

    // Process reservations data voor beschikbaarheidscheck (zelfde logica als SimpleMaterialWizard)
    const processReservationsData = useMemo(() => {
        const reservationsData = {};

        if (Array.isArray(reservations)) {
            reservations.forEach(record => {
                if (record.fields && record.fields.Materiaal && Array.isArray(record.fields.Materiaal)) {
                    // Get the date range for this reservation
                    const startDate = new Date(record.fields.Start);
                    const endDate = new Date(record.fields.Eind);
                    const draaidagNaam = record.fields.DraaidagNaam?.[0] || record.fields.DraaidagNaamOutlook?.[0] || 'Onbekende draaidag';

                    record.fields.Materiaal.forEach(materialId => {
                        if (!reservationsData[materialId]) {
                            reservationsData[materialId] = [];
                        }

                        // Store reservation info with draaidag name
                        reservationsData[materialId].push({
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            draaidagNaam: draaidagNaam,
                            reservationId: record.id
                        });
                    });
                }
            });
        }

        return reservationsData;
    }, [reservations]);

    const handleToggleMaterial = (materialId) => {
        setSelectedMaterials(prev => {
            if (prev.includes(materialId)) {
                return prev.filter(id => id !== materialId);
            } else {
                return [...prev, materialId];
            }
        });
    };

    const handleCopy = () => {
        // Geef de volledige materiaal objecten door
        const materialsToSave = selectedMaterials
            .map(id => draaidagMaterials[id])
            .filter(Boolean);

        onCopy(materialsToSave);
        onClose();
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: { xs: 0, sm: '0.75rem' } } }}>
            <DialogTitle sx={{ fontFamily: T.font, fontWeight: 700, color: T.text }}>
                Kopieer materialen van een andere draaidag
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        placeholder="Zoek op naam of datum..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon name="search" size={20} sx={{ color: T.outline }} />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '0.5rem',
                                bgcolor: T.surfaceLow,
                                fontFamily: T.fontBody,
                                '& fieldset': { borderColor: T.outlineVariant },
                                '&:hover fieldset': { borderColor: T.outline },
                                '&.Mui-focused fieldset': { borderColor: T.primary },
                            },
                            '& .MuiInputBase-input': { fontFamily: T.fontBody },
                        }}
                    />
                </Box>

                {!selectedDraaidag ? (
                    <>
                        <Typography variant="body2" sx={{ color: T.outline, fontFamily: T.fontBody, mb: 1 }}>
                            Selecteer een draaidag om materialen van te kopiëren:
                        </Typography>
                        <List>
                            {filteredDraaidagen.length === 0 ? (
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Typography sx={{ fontFamily: T.fontBody, color: T.text }}>
                                                Geen draaidagen gevonden met materialen
                                            </Typography>
                                        }
                                        secondary={searchTerm ? (
                                            <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                                                Probeer een andere zoekterm
                                            </Typography>
                                        ) : null}
                                    />
                                </ListItem>
                            ) : (
                                filteredDraaidagen.map((draaidag) => (
                                    <ListItem
                                        key={draaidag.id}
                                        button
                                        onClick={() => setSelectedDraaidag(draaidag)}
                                        sx={{
                                            borderRadius: '0.5rem',
                                            mb: 0.5,
                                            '&:hover': { bgcolor: T.surfaceLow },
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography sx={{ fontFamily: T.fontBody, fontWeight: 500, color: T.text }}>
                                                    {draaidag.naam}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                                                    {formatDate(draaidag.datum)} - {draaidag.materiaalIds.size} materialen
                                                </Typography>
                                            }
                                        />
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Box
                                component="button"
                                onClick={() => {
                                    setSelectedDraaidag(null);
                                    setSelectedMaterials([]);
                                    setDraaidagMaterials({});
                                }}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1.5,
                                    py: 0.75,
                                    border: `1px solid ${T.outlineVariant}`,
                                    borderRadius: '0.5rem',
                                    bgcolor: T.surface,
                                    color: T.primary,
                                    fontFamily: T.fontBody,
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    '&:hover': { bgcolor: T.surfaceLow, borderColor: T.primary },
                                }}
                            >
                                <Icon name="arrow_back" size={16} />
                                Terug naar draaidagen
                            </Box>
                        </Box>

                        <Typography variant="h6" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, mb: 0.5 }}>
                            {selectedDraaidag.naam}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, mb: 1 }}>
                            {formatDate(selectedDraaidag.datum)}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {isLoadingMaterials || isCheckingAvailability ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CircularProgress size={32} sx={{ color: T.primary }} />
                                <Typography variant="body2" sx={{ mt: 2, fontFamily: T.fontBody, color: T.outline }}>
                                    {isLoadingMaterials ? 'Materialen laden...' : 'Beschikbaarheid controleren...'}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {Object.keys(availabilityMap).length > 0 && (
                                    <>
                                        {/* Info alert styled as Stitch box */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 2,
                                            py: 1.5,
                                            mb: 2,
                                            borderRadius: '0.5rem',
                                            bgcolor: T.primaryContainer,
                                            border: `1px solid ${T.outlineVariant}`,
                                        }}>
                                            <Icon name="info" size={20} sx={{ color: T.primary }} />
                                            <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.primaryDim }}>
                                                {Object.values(availabilityMap).filter(v => v).length} van {Object.keys(availabilityMap).length} materialen beschikbaar
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                            <Box
                                                component="button"
                                                onClick={() => {
                                                    // Selecteer alle beschikbare materialen
                                                    const availableMaterials = Array.from(selectedDraaidag.materiaalIds)
                                                        .filter(id => availabilityMap[id] !== false);
                                                    setSelectedMaterials(availableMaterials);
                                                }}
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.75,
                                                    border: `1px solid ${T.outlineVariant}`,
                                                    borderRadius: '0.5rem',
                                                    bgcolor: T.surface,
                                                    color: T.primary,
                                                    fontFamily: T.fontBody,
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': { bgcolor: T.surfaceLow, borderColor: T.primary },
                                                }}
                                            >
                                                Selecteer alles
                                            </Box>
                                            <Box
                                                component="button"
                                                onClick={() => setSelectedMaterials([])}
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.75,
                                                    border: `1px solid ${T.outlineVariant}`,
                                                    borderRadius: '0.5rem',
                                                    bgcolor: T.surface,
                                                    color: T.primary,
                                                    fontFamily: T.fontBody,
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': { bgcolor: T.surfaceLow, borderColor: T.primary },
                                                }}
                                            >
                                                Deselecteer alles
                                            </Box>
                                        </Box>
                                    </>
                                )}

                                {/* Materialen gegroepeerd per categorie */}
                                {Object.entries(materialsByCategory).map(([category, materials]) => (
                                    <Accordion
                                        key={category}
                                        expanded={expandedCategories.has(category)}
                                        onChange={() => handleToggleCategory(category)}
                                        sx={{
                                            mb: 1,
                                            borderRadius: '0.75rem !important',
                                            boxShadow: T.shadow,
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': { margin: '0 0 8px 0' },
                                        }}
                                    >
                                        <AccordionSummary expandIcon={<Icon name="expand_more" size={20} sx={{ color: T.outline }} />}>
                                            <Typography sx={{ fontFamily: T.font, fontWeight: 600, color: T.text, fontSize: '0.875rem' }}>
                                                {category} ({materials.length})
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <List dense>
                                                {materials.map(({ id: materialId, material }) => {
                                                    const isAvailable = availabilityMap[materialId] !== false;

                                                    return (
                                                        <ListItem
                                                            key={materialId}
                                                            dense
                                                            disabled={!isAvailable}
                                                            sx={{
                                                                opacity: isAvailable ? 1 : 0.5,
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={selectedMaterials.includes(materialId)}
                                                                onChange={() => handleToggleMaterial(materialId)}
                                                                disabled={!isAvailable}
                                                                sx={{
                                                                    color: T.outlineVariant,
                                                                    '&.Mui-checked': { color: T.primary },
                                                                }}
                                                            />
                                                            <ListItemText
                                                                primary={
                                                                    <Typography sx={{ fontFamily: T.fontBody, fontWeight: 500, color: T.text, fontSize: '0.875rem' }}>
                                                                        {material.fields.ID}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, fontSize: '0.75rem' }}>
                                                                        {material.fields.Omschrijving}
                                                                    </Typography>
                                                                }
                                                            />
                                                            {!isAvailable && (
                                                                <Typography
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        px: 1.5,
                                                                        py: 0.25,
                                                                        borderRadius: '9999px',
                                                                        bgcolor: T.errorBg,
                                                                        color: T.error,
                                                                        fontFamily: T.fontBody,
                                                                        fontSize: '0.6875rem',
                                                                        fontWeight: 600,
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    Niet beschikbaar
                                                                </Typography>
                                                            )}
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <Box
                    component="button"
                    onClick={onClose}
                    sx={{
                        px: 2.5,
                        py: 1,
                        border: `1px solid ${T.outlineVariant}`,
                        borderRadius: '0.5rem',
                        bgcolor: T.surface,
                        color: T.secondary,
                        fontFamily: T.fontBody,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: T.surfaceLow, borderColor: T.primary },
                    }}
                >
                    Annuleren
                </Box>
                {selectedDraaidag && (
                    <Box
                        component="button"
                        onClick={handleCopy}
                        disabled={selectedMaterials.length === 0 || isLoadingMaterials || isCheckingAvailability}
                        sx={{
                            px: 2.5,
                            py: 1,
                            border: 'none',
                            borderRadius: '0.5rem',
                            background: (selectedMaterials.length === 0 || isLoadingMaterials || isCheckingAvailability)
                                ? T.outlineVariant
                                : `linear-gradient(135deg, ${T.primary}, ${T.primaryDim})`,
                            color: T.surface,
                            fontFamily: T.fontBody,
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: (selectedMaterials.length === 0 || isLoadingMaterials || isCheckingAvailability) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s ease',
                            opacity: (selectedMaterials.length === 0 || isLoadingMaterials || isCheckingAvailability) ? 0.6 : 1,
                            '&:hover': {
                                opacity: (selectedMaterials.length === 0 || isLoadingMaterials || isCheckingAvailability) ? 0.6 : 0.9,
                            },
                        }}
                    >
                        Kopieer {selectedMaterials.length} materialen
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CopyMaterialsDialog;
